import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Typography,
  Skeleton,
} from '@mui/material';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { WorkItem } from '../workitems/workItemTypes';
import {
  getCustomerTickets,
  PaginationInfo,
} from '../../services/ticketDataService';

const REPAIRSHOPR_BASE_URL = 'https://techsolvers.repairshopr.com';
const PAGE_SIZE = 10;

interface PastTicketsTableProps {
  customerId: string | null;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
};

export const PastTicketsTable: React.FC<PastTicketsTableProps> = ({ customerId }) => {
  const [tickets, setTickets] = useState<WorkItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = useCallback(
    async (pageNum: number, signal: AbortSignal) => {
      if (!customerId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await getCustomerTickets(customerId, pageNum, PAGE_SIZE, signal);
        setTickets(response.items ?? []);
        setPagination(response.pagination ?? null);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load tickets');
      } finally {
        setLoading(false);
      }
    },
    [customerId]
  );

  useEffect(() => {
    if (!customerId) {
      setTickets([]);
      setPagination(null);
      return;
    }

    const controller = new AbortController();
    setPage(1);
    fetchTickets(1, controller.signal);

    return () => controller.abort();
  }, [customerId, fetchTickets]);

  useEffect(() => {
    if (!customerId || page === 1) return;

    const controller = new AbortController();
    fetchTickets(page, controller.signal);

    return () => controller.abort();
  }, [page, customerId, fetchTickets]);

  const handleRowClick = (ticket: WorkItem) => {
    window.open(`${REPAIRSHOPR_BASE_URL}/tickets/${ticket.id}`, '_blank');
  };

  if (!customerId) {
    return (
      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600, color: 'text.primary' }}>
          Past Tickets
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select a customer to view their past tickets.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, border: '1px solid', borderColor: 'grey.200' }}>
      <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600, color: 'text.primary' }}>
        Past Tickets
      </Typography>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Number</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton variant="text" width={80} /></TableCell>
                  <TableCell><Skeleton variant="text" width={50} /></TableCell>
                  <TableCell><Skeleton variant="text" width={200} /></TableCell>
                  <TableCell><Skeleton variant="rounded" width={80} height={24} /></TableCell>
                </TableRow>
              ))
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No past tickets found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  hover
                  onClick={() => handleRowClick(ticket)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(ticket.createdAt)}
                  </TableCell>
                  <TableCell>{ticket.number ?? '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ticket.title || '-'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const s = ticket.jobStatus || ticket.status;
                      return s ? s.charAt(0).toUpperCase() + s.slice(1) : '-';
                    })()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && pagination.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {pagination.totalItems} ticket{pagination.totalItems !== 1 ? 's' : ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<IconChevronLeft size={16} stroke={1.5} />}
              onClick={() => setPage((p) => p - 1)}
              disabled={!pagination.hasPrevious}
            >
              Previous
            </Button>
            <Button
              size="small"
              endIcon={<IconChevronRight size={16} stroke={1.5} />}
              onClick={() => setPage((p) => p + 1)}
              disabled={!pagination.hasNext}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};
